/**
 * SwitchTopologyCanvas
 * Hierarchical tree SVG: this switch at top, connected switches in a row below.
 * Each edge shows local port → remote port.
 * Edges coloured by RSTP role. Nodes are draggable.
 *
 * Props:
 *   element  – rack element object (current switch)
 *   ports    – switch port array from SwitchPortsTab (already fetched)
 */

import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';

// ── RSTP edge style ────────────────────────────────────────────────────
// Read CSS custom properties at render time for SVG inline styles
const _css = (v) => getComputedStyle(document.documentElement).getPropertyValue(v).trim();

const RSTP_EDGE = {
  root:       { stroke: _css('--rstp-root')       || '#2563eb', dash: null,    w: 2.5 },
  designated: { stroke: _css('--rstp-designated') || '#3b82f6', dash: null,    w: 2   },
  alternate:  { stroke: _css('--rstp-alternate')  || '#f59e0b', dash: '7 3',   w: 1.5 },
  backup:     { stroke: _css('--rstp-backup')     || '#b45309', dash: '4 2',   w: 1.5 },
  disabled:   { stroke: _css('--rstp-disabled')   || '#9ca3af', dash: '2 4',   w: 1   },
};
const DEF_EDGE = { stroke: 'var(--text-muted)', dash: null, w: 1.5 };

// ── Node dimensions ────────────────────────────────────────────────────
const NW    = 130;  // node width
const NH    = 44;   // node height
const H_GAP = 24;   // horizontal gap between siblings
const V_GAP = 88;   // vertical gap between levels

// ── Component ─────────────────────────────────────────────────────────
function SwitchTopologyCanvas({ element, ports }) {

  // ── Build graph ─────────────────────────────────────────────────────
  const { selfNode, peerNodes, edges, uplinks } = useMemo(() => {
    const self = {
      id:   element.id,
      name: element.name || element.code || `SW ${element.id}`,
    };
    const peerMap   = new Map();
    const edgeList  = [];
    const uplinkList = [];

    for (const port of ports) {
      const conn  = port.connection;
      const other = conn?.other_end;

      if (other?.element_type === 'switch') {
        const rid = other.element_id;
        if (!peerMap.has(rid)) {
          peerMap.set(rid, { id: rid, name: other.element_name || `SW ${rid}` });
        }
        edgeList.push({
          key:        `e-${port.id}`,
          peerId:     rid,
          localPort:  port.label && port.label !== `Port ${port.port_number}` ? port.label : `P${port.port_number}`,
          remotePort: other.port_number != null ? `P${other.port_number}` : '?',
          rstp:       port.rstp_role || '',
        });
      } else if (port.destination_type === 'uplink' && port.connected_to) {
        uplinkList.push({
          key:       `ul-${port.id}`,
          localPort: port.label && port.label !== `Port ${port.port_number}` ? port.label : `P${port.port_number}`,
          text:      port.connected_to,
          rstp:      port.rstp_role || '',
        });
      }
    }

    return { selfNode: self, peerNodes: [...peerMap.values()], edges: edgeList, uplinks: uplinkList };
  }, [element, ports]);

  // ── Compute SVG dimensions & tree positions ──────────────────────────
  const { svgW, svgH, initPos } = useMemo(() => {
    const peerCount   = peerNodes.length;
    const uplinkCount = uplinks.length;
    const totalCols   = peerCount + (uplinkCount > 0 ? uplinkCount : 0);

    const rowW = Math.max(1, totalCols) * (NW + H_GAP) - H_GAP;
    const w    = Math.max(rowW + 60, NW + 60);
    const h    = totalCols > 0 ? V_GAP + NH + 20 : NH + 20;

    const selfX = w / 2 - NW / 2;
    const selfY = 16;

    const pos = new Map();
    pos.set(element.id, { x: selfX, y: selfY });

    // Peers: evenly spaced in bottom row
    const totalPeerW = peerCount * NW + Math.max(0, peerCount - 1) * H_GAP;
    const startX = w / 2 - totalPeerW / 2;
    peerNodes.forEach((p, i) => {
      pos.set(p.id, { x: startX + i * (NW + H_GAP), y: selfY + V_GAP });
    });

    // Uplinks: to the right of peers
    const ulStartX = peerCount > 0 ? startX + peerCount * (NW + H_GAP) : w / 2 - (uplinkCount * (NW + H_GAP)) / 2;
    uplinks.forEach((ul, i) => {
      pos.set(ul.key, { x: ulStartX + i * (NW + H_GAP), y: selfY + V_GAP });
    });

    return { svgW: w, svgH: h, initPos: pos };
  }, [peerNodes, uplinks, element.id]);

  const [positions, setPositions] = useState(initPos);
  useEffect(() => { setPositions(initPos); }, [initPos]);

  // ── Drag ──────────────────────────────────────────────────────────────
  const svgRef      = useRef(null);
  const draggingRef = useRef(null);

  const toSvgPt = (e) => {
    const pt = svgRef.current.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    return pt.matrixTransform(svgRef.current.getScreenCTM().inverse());
  };

  const onNodeDown = (e, nodeId) => {
    e.preventDefault(); e.stopPropagation();
    const pt   = toSvgPt(e);
    const curr = positions.get(nodeId) || { x: 0, y: 0 };
    draggingRef.current = { id: nodeId, ox: curr.x, oy: curr.y, mx: pt.x, my: pt.y };
  };

  const onMove = useCallback((e) => {
    if (!draggingRef.current || !svgRef.current) return;
    const { id, ox, oy, mx, my } = draggingRef.current;
    const pt = toSvgPt(e);
    setPositions(prev => { const n = new Map(prev); n.set(id, { x: ox + pt.x - mx, y: oy + pt.y - my }); return n; });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onUp = useCallback(() => { draggingRef.current = null; }, []);

  // ── Early-out ─────────────────────────────────────────────────────────
  if (!edges.length && !uplinks.length) {
    return (
      <div style={{ padding: '0.6rem 1rem', color: 'var(--text-muted)', fontSize: '0.78rem', background: 'var(--bg-toolbar)', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 8 }}>
        Sin conexiones entre switches ni uplinks en este switch.
      </div>
    );
  }

  const getES  = (rstp) => RSTP_EDGE[rstp] || DEF_EDGE;
  const trunc  = (s, n) => s && s.length > n ? s.slice(0, n - 1) + '…' : (s || '');
  const selfPos = positions.get(element.id) || { x: 0, y: 16 };

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', background: 'var(--bg-toolbar)', marginBottom: 10 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 10px', background: 'var(--bg-toolbar)', borderBottom: '1px solid var(--border)' }}>
        <span style={{ fontWeight: 700, fontSize: '0.72rem', color: 'var(--text-primary)' }}>🌐 Topología</span>
        <span style={{ flex: 1 }} />
        {/* RSTP legend — only roles actually used */}
        {[
          ['root','Root',_css('--rstp-root'),null],['designated','Design.',_css('--rstp-designated'),null],
          ['alternate','Alt.',_css('--rstp-alternate'),'7 3'],['backup','BKP',_css('--rstp-backup'),'4 2'],['disabled','Dis.',_css('--rstp-disabled'),'2 4'],
        ].filter(([k]) => edges.some(e => e.rstp === k)).map(([k,lbl,col,dash]) => (
          <span key={k} style={{ display:'flex', alignItems:'center', gap:2, fontSize:'0.62rem', color:'var(--text-primary)' }}>
            <svg width="14" height="5" style={{ display:'block' }}>
              <line x1="0" y1="2.5" x2="14" y2="2.5" stroke={col} strokeWidth="2" strokeDasharray={dash||undefined}/>
            </svg>{lbl}
          </span>
        ))}
      </div>

      {/* SVG */}
      <svg
        ref={svgRef}
        width="100%"
        viewBox={`0 0 ${svgW} ${svgH}`}
        style={{ display: 'block', userSelect: 'none', cursor: draggingRef.current ? 'grabbing' : 'default', maxHeight: 190 }}
        onMouseMove={onMove}
        onMouseUp={onUp}
        onMouseLeave={onUp}
      >
        <defs>
          <filter id="tsh" x="-15%" y="-15%" width="130%" height="145%">
            <feDropShadow dx="0" dy="1" stdDeviation="1.5" floodColor="#00000018"/>
          </filter>
        </defs>

        {/* ── Switch ↔ Switch edges ── */}
        {edges.map(edge => {
          const peer = positions.get(edge.peerId);
          if (!peer) return null;
          const es = getES(edge.rstp);
          const x1 = selfPos.x + NW / 2, y1 = selfPos.y + NH;
          const x2 = peer.x   + NW / 2, y2 = peer.y;
          const mx = (x1 + x2) / 2,     my = (y1 + y2) / 2;
          const lbl = `${edge.localPort} → ${edge.remotePort}`;
          const lblW = lbl.length * 5.6 + 10;
          return (
            <g key={edge.key}>
              <line x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={es.stroke} strokeWidth={es.w}
                strokeDasharray={es.dash || undefined} opacity={0.8}/>
              <rect x={mx - lblW / 2} y={my - 8} width={lblW} height={15} rx={4}
                fill="var(--node-edge-label-bg)" stroke={es.stroke} strokeWidth={0.7} opacity={0.95}/>
              <text x={mx} y={my + 3.5} textAnchor="middle" fontSize={8.5} fontWeight="600" fill={es.stroke}>
                {lbl}
              </text>
            </g>
          );
        })}

        {/* ── External uplink stubs ── */}
        {uplinks.map(ul => {
          const pos = positions.get(ul.key);
          if (!pos) return null;
          const es = getES(ul.rstp);
          const x1 = selfPos.x + NW / 2, y1 = selfPos.y + NH;
          const x2 = pos.x + NW / 2,     y2 = pos.y;
          const mx = (x1 + x2) / 2,      my = (y1 + y2) / 2;
          return (
            <g key={ul.key}>
              <line x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={es.stroke} strokeWidth={es.w || 1.5}
                strokeDasharray={es.dash || '5 3'} opacity={0.6}/>
              {/* port label on line */}
              <rect x={mx - 14} y={my - 7} width={28} height={13} rx={3}
                fill="var(--node-edge-label-bg)" stroke={es.stroke} strokeWidth={0.7} opacity={0.9}/>
              <text x={mx} y={my + 3} textAnchor="middle" fontSize={8.5} fontWeight="600" fill={es.stroke}>
                {ul.localPort}
              </text>
              {/* cloud stub */}
              <rect x={pos.x} y={pos.y} width={NW} height={NH} rx={8}
                fill="var(--uplink-bg)" stroke="var(--uplink-stroke)" strokeWidth={1.2}/>
              <text x={pos.x + NW / 2} y={pos.y + 16} textAnchor="middle" fontSize={9} fill="var(--uplink-text)" fontWeight="700">
                ⬆ {trunc(ul.text, 16)}
              </text>
              <text x={pos.x + NW / 2} y={pos.y + 30} textAnchor="middle" fontSize={8} fill="var(--uplink-sub)">
                uplink externo
              </text>
            </g>
          );
        })}

        {/* ── Self node ── */}
        <g transform={`translate(${selfPos.x},${selfPos.y})`}
          style={{ cursor: 'grab' }}
          onMouseDown={e => onNodeDown(e, element.id)}>
          <rect width={NW} height={NH} rx={8}
            fill="var(--node-self-bg)" stroke="var(--node-self-stroke)" strokeWidth={2} filter="url(#tsh)"/>
          <text x={NW / 2} y={17} textAnchor="middle" fontSize={11} fontWeight="700" fill="var(--node-self-text)">
            {trunc(selfNode.name, 15)}
          </text>
          <text x={NW / 2} y={31} textAnchor="middle" fontSize={9} fill="var(--node-self-sub)">
            este switch
          </text>
        </g>

        {/* ── Peer nodes ── */}
        {peerNodes.map(peer => {
          const pos = positions.get(peer.id);
          if (!pos) return null;
          const linkCount = edges.filter(e => e.peerId === peer.id).length;
          return (
            <g key={peer.id}
              transform={`translate(${pos.x},${pos.y})`}
              style={{ cursor: 'grab' }}
              onMouseDown={e => onNodeDown(e, peer.id)}>
              <rect width={NW} height={NH} rx={8}
                fill="var(--bg-card)" stroke="var(--text-muted)" strokeWidth={1.5} filter="url(#tsh)"/>
              <text x={NW / 2} y={17} textAnchor="middle" fontSize={11} fontWeight="700" fill="var(--text-primary)">
                {trunc(peer.name, 15)}
              </text>
              <text x={NW / 2} y={31} textAnchor="middle" fontSize={9} fill="var(--text-muted)">
                {linkCount} enlace{linkCount !== 1 ? 's' : ''}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export default SwitchTopologyCanvas;
